import { Button } from "antd";
import { PanelLeftClose, PanelRightClose } from "lucide-react";
import { Panel, PanelResizeHandle } from "react-resizable-panels";
import { useCollaborative, Comment } from "../use-collaborative";
import { CommentsList } from "./CommentsList";
import { useEffect } from "react";

interface CommentsPanelProps {
  commentsOpen: boolean;
  setCommentsOpen: (open: boolean) => void;
  /** Persisted comments (topics: output.comments) — drives the "any comments?" gate + list skeleton. */
  comments?: Comment[];
  /** When true (topics streaming), skip the list loading skeleton. */
  isStreaming?: boolean;
  /** Whether the current user may author comments / replies. */
  canComment?: boolean;
  /** Whether to show the resolve/reopen control (building blocks only; topics
   *  leaves this false so its comment UI is unchanged). */
  canResolve?: boolean;
}

export const CommentsPanel = ({
  commentsOpen,
  setCommentsOpen,
  comments,
  isStreaming = false,
  canComment = true,
  canResolve = false
}: CommentsPanelProps) => {
  const { localComments, commentsInEditMode } = useCollaborative();

  useEffect(() => {
    if (commentsInEditMode.length > 0) {
      setCommentsOpen(true);
    }
  }, [commentsInEditMode]);

  if (!comments?.length && (!localComments || localComments.length === 0)) {
    return null;
  }

  if (!commentsOpen) {
    return (
      <div className="side-rail" onClick={() => setCommentsOpen(true)}>
        <div className="rail-content">
          <PanelLeftClose className="rail-icon" />
          <span className="rail-label rail-label-vertical">Open Comments</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PanelResizeHandle className="resize-handle" />
      <Panel defaultSize={20} minSize={15} maxSize={35} className="panel">
        <div className="h-full overflow-y-auto p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">Comments</span>
            <Button
              type="text"
              size="small"
              className="icon-button-muted"
              onClick={() => setCommentsOpen(false)}
            >
              <PanelRightClose className="icon-16" />
            </Button>
          </div>
          <CommentsList
            comments={comments}
            isStreaming={isStreaming}
            canComment={canComment}
            canResolve={canResolve}
          />
        </div>
      </Panel>
    </>
  );
};
